import type { CapaRecord } from '@/types/cgt'

/**
 * One row on the CAPA page is one CAPA — not one procedure.
 *
 * A CAPA is a capa-category form raised inside a running procedure ("+" →
 * Create CAPA → pick a form), so a run can hold several and each has its own
 * Event ID. The list endpoint still pages PROCEDURES, so the page fans each one
 * out over its `capaRecords` here, and every procedure-level column — WO#, Qty,
 * Progress, Started, Due — is carried onto each of that run's rows unchanged.
 *
 * Kept out of the component because the fan-out has two edges worth pinning: the
 * key has to stop being the procedure id, and a procedure with no records at all
 * must not vanish.
 */

/** A row identified by its procedure, before the fan-out. */
interface ProcedureShaped {
  id: string
}

export interface CapaRowExtras {
  /**
   * What makes this row unique in the table — `<procedureId>:<submissionId>`.
   *
   * The procedure id no longer can be: two CAPAs in one run share it, and React
   * keys, the expand toggle and the pick-list menu all key on row identity. They
   * key on this instead, so expanding one CAPA does not expand its sibling.
   */
  rowKey: string
  /** The CAPA this row is, or null when the run reported none (see below). */
  capa: CapaRecord | null
}

export function expandCapaRows<T extends ProcedureShaped>(
  rows: T[],
  recordsOf: (row: T) => CapaRecord[] | undefined,
): Array<T & CapaRowExtras> {
  return rows.flatMap((row): Array<T & CapaRowExtras> => {
    const records = recordsOf(row) ?? []
    /**
     * No records, but the server listed the run: keep ONE row showing dashes.
     *
     * It should not happen — the list only returns procedures holding a CAPA
     * form — so the case that produces it is a version skew, an older API that
     * does not send `capaRecords` at all. Rendering nothing would empty the page
     * and read as "no CAPA work", which is worse than a row that says the run
     * has CAPA work whose details this build could not read.
     */
    if (records.length === 0) return [{ ...row, rowKey: row.id, capa: null }]
    return records.map((capa) => ({ ...row, rowKey: `${row.id}:${capa.id}`, capa }))
  })
}

/** `capa form - transplant (V1)`, or a dash when the row has no CAPA to name. */
export function capaFormLabel(capa: CapaRecord | null): string {
  if (!capa) return '—'
  const name = capa.formName.trim() || 'Untitled form'
  return `${name} (V${capa.formVersion})`
}

/**
 * Who raised a CAPA, as the Created By column shows them.
 *
 * The stored `createdBy` is an ACTOR STRING — `actorOf(user)` server-side, so an
 * email address in practice — and the column was rendering it raw. A quality
 * record's author has to be both readable and identifiable, which is two facts
 * and not one: the NAME is what the column shows, the ADDRESS is what its
 * tooltip carries, so a reader can still tell two people with the same name
 * apart without the table paying for it in width.
 *
 * `email` is null whenever the tooltip would add nothing — an actor that never
 * resolved to a name is already being shown in full, and repeating it on hover
 * is noise.
 */
export function capaAuthor(capa: CapaRecord | null): { label: string; email: string | null } {
  /**
   * `system` is a real stored value — a form opened by a job rather than a
   * person — and reads better as a dash than as a username nobody has.
   */
  if (!capa || !capa.createdBy.trim() || capa.createdBy === 'system') {
    return { label: '—', email: null }
  }
  const actor = capa.createdBy.trim()
  const name = capa.createdByName?.trim()
  // No name resolved: the author has left the roster, or the backend predates
  // the resolution. The raw actor is still more use than a dash — "raised by
  // someone no longer here" and "raised by nobody" are different facts.
  if (!name || name.toLowerCase() === actor.toLowerCase()) return { label: actor, email: null }
  return { label: name, email: actor }
}
